import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Box } from "@mui/material";

import { ActionButton, FormDialog, InputField, SelectField } from "../v3";
import { ProjectService } from "../../services";
import { projectStageValidationRules } from "../../pages/project/validation";

export const AddEditProjectStage = ({ open, onClose = () => {}, projectStageId, onSuccess = () => {} }) => {
  const [projectStages, setProjectStages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedStage, setSelectedStage] = useState(null);
  const [projectStageDetails, setProjectStageDetails] = useState({
    stageId: undefined,
    estimatedAmount: undefined,
  });

  const { id } = useParams();

  useEffect(() => {
    if (!open) return;

    if (projectStageId) fetchData(projectStageId);
    else handleClearForm();

    if (id) fetchProjectStages();
  }, [open, projectStageId, id]);

  const fetchData = async (stageId) => {
    try {
      const { data } = await ProjectService.getProjectStageById(stageId);
      setSelectedStage(data.stage);
      setProjectStageDetails({
        stageId: data?.stageId,
        estimatedAmount: data?.estimatedAmount,
      });
    } catch (error) {
      toast.error("Failed to fetch stage details");
      console.error("Error fetching stage data:", error);
    }
  };

  const fetchProjectStages = async () => {
    try {
      const { data: projectStages } = await ProjectService.getProjectStages({ projectId: id });
      setProjectStages(projectStages.rows);
    } catch (error) {
      console.error("Error fetching project stages:", error);
      toast.error("Failed to fetch project stages");
    }
  };

  const updateDetailsState = (event) => {
    const { name, value } = event.target;
    setProjectStageDetails((prev) => ({ ...prev, [name]: value }));

    if (name === "estimatedAmount" && value !== "" && parseFloat(value) < 0) {
      setErrors((prev) => ({ ...prev, estimatedAmount: "must be greater than 0" }));
    } else {
      setErrors((prev) => {
        const { estimatedAmount, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleStageChange = (selected) => {
    setSelectedStage(selected);
    setProjectStageDetails((prev) => ({ ...prev, stageId: selected?.id }));
  };
  const handleClearForm = () => {
    setProjectStageDetails({
      stageId: undefined,
      estimatedAmount: undefined,
    });
    setSelectedStage(null);
  };

  const handleCloseModal = () => {
    handleClearForm();
    setErrors({});
    onClose();
  };

  const handleSavePhase = async () => {
    setErrors({});
    const errors = projectStageValidationRules(projectStageDetails);
    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    if (projectStageDetails.estimatedAmount) {
      projectStageDetails.estimatedAmount = parseFloat(projectStageDetails.estimatedAmount);
    }

    setLoading(true);
    try {
      if (projectStageId) await ProjectService.updateProjectStage(projectStageId, projectStageDetails);
      else
        await ProjectService.createProjectStage({
          ...projectStageDetails,
          projectId: Number(id),
        });

      toast.success(`Phase ${projectStageId ? "updated" : "added"} successfully`);
      onSuccess();
    } catch (error) {
      console.error("Error saving phase:", error);
      toast.error("Failed to save phase. Please try again.");
    } finally {
      setLoading(false);
      handleCloseModal();
    }
  };
  return (
    <FormDialog
      open={open}
      title={projectStageId ? "Edit Phase" : "Add Phase"}
      actions={
        <>
          <ActionButton buttonText="Cancel" onButtonClick={handleCloseModal} variant="outlined" />
          <ActionButton buttonText="Save" onButtonClick={handleSavePhase} loading={loading} />
        </>
      }
    >
      <Box mt={1}>
        <Box display="flex" gap={2}>
          <Box>
            <SelectField
              label="Stage"
              value={selectedStage}
              getData={ProjectService.getStages}
              optionFilter={(allStages) =>
                allStages.filter((stage) => !projectStages.some((projectStage) => projectStage.stageId === stage.id))
              }
              disabled={!!projectStageId}
              required
              error={!!errors.stageId}
              helperText={errors.stageId}
              onChange={handleStageChange}
              style={{ width: "250px" }}
            />
          </Box>
          <Box>
            <InputField
              required
              type="number"
              label="Estimated Amount"
              name="estimatedAmount"
              value={projectStageDetails.estimatedAmount}
              onChange={updateDetailsState}
              error={!!errors.estimatedAmount}
              helperText={errors.estimatedAmount}
              style={{ width: "250px" }}
            />
          </Box>
        </Box>
      </Box>
    </FormDialog>
  );
};
